// Vendor Program v2 PR 2 — small accessible 1–4 star widgets for the
// borrower sign-off. Reimplemented portal-side (the staff RatingStars in
// components/portfolioAdmin isn't on the portal import allow-list).
//
// BorrowerStarRating = the interactive picker: radio-group semantics with
// roving tabindex + arrow-key movement, per-star aria-labels ("1 of 4
// stars"), click or keyboard selects. BorrowerStarsDisplay = the read-only
// rendering used by signed-off summaries and the report history rows.

import { useRef } from "react";
import type { KeyboardEvent } from "react";
import { Star } from "lucide-react";

export const MAX_STARS = 4;

const STAR_VALUES = Array.from({ length: MAX_STARS }, (_, i) => i + 1);

export function BorrowerStarRating({
  label,
  value,
  onChange,
  disabled,
  testId,
}: {
  label: string;
  /** 1–4, or null when not yet rated. */
  value: number | null;
  onChange: (value: number) => void;
  disabled?: boolean;
  testId?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const select = (n: number) => {
    if (disabled) return;
    onChange(n);
    refs.current[n - 1]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, n: number) => {
    let next: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      next = n >= MAX_STARS ? 1 : n + 1;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      next = n <= 1 ? MAX_STARS : n - 1;
    }
    if (next !== null) {
      e.preventDefault();
      select(next);
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex items-center gap-space-1"
      data-testid={testId}
    >
      {STAR_VALUES.map((n) => {
        const filled = value !== null && n <= value;
        return (
          <button
            key={n}
            ref={(el) => {
              refs.current[n - 1] = el;
            }}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} of ${MAX_STARS} stars`}
            // Roving tabindex: the chosen star is the tab stop; before any
            // choice the first star is, so the row stays keyboard-reachable.
            tabIndex={value === n || (value === null && n === 1) ? 0 : -1}
            disabled={disabled}
            onClick={() => select(n)}
            onKeyDown={(e) => onKeyDown(e, n)}
            className="p-space-1 rounded-md transition-colors duration-fast ease-standard focus:outline-none focus-visible:shadow-goldRing disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Star
              size={20}
              className={filled ? "text-gold fill-gold" : "text-gray-mid"}
              aria-hidden="true"
            />
          </button>
        );
      })}
    </div>
  );
}

export function BorrowerStarsDisplay({
  value,
  label,
  testId,
}: {
  /** May be fractional (e.g. the server-computed overall) — rounds. */
  value: number;
  label?: string;
  testId?: string;
}) {
  const filled = Math.max(0, Math.min(MAX_STARS, Math.round(value)));
  return (
    <span
      role="img"
      aria-label={label ?? `${value} of ${MAX_STARS} stars`}
      className="inline-flex items-center gap-space-1"
      data-testid={testId}
    >
      {STAR_VALUES.map((n) => (
        <Star
          key={n}
          size={14}
          className={n <= filled ? "text-gold fill-gold" : "text-gray-mid"}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}
